import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check, Minus, Plus, ArrowLeft, ArrowRight, Ticket,
  Calendar, Users, Mail, Phone, ShoppingCart, Lock, X, Tag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useHotel } from '../context/HotelContext';
import { useAuth } from '../context/AuthContext';
import apiClient from '../lib/apiClient';

const STEPS = ['Select Tickets', 'Your Details', 'Confirm & Pay'];

/* ─────────────────────────────────────────────────────────────
   Ticket Card
───────────────────────────────────────────────────────────── */
const TicketCard = ({ ticket, qty, onQtyChange }) => {
  const isSelected = qty > 0;
  const color = ticket.color || '#2563EB';

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden flex flex-col transition-all duration-200"
      style={{
        boxShadow: isSelected
          ? `0 8px 32px ${color}30, 0 0 0 2px ${color}`
          : '0 2px 12px rgba(0,0,0,0.08)',
        transform: isSelected ? 'scale(1.005)' : 'scale(1)',
      }}
    >
      {/* Image */}
      {ticket.imageUrl && (
        <div className="relative h-48 w-full overflow-hidden flex-shrink-0">
          <img src={ticket.imageUrl} alt={ticket.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <span
            className="absolute bottom-3 right-3 text-white text-xs font-semibold px-3 py-1 rounded-full backdrop-blur-sm"
            style={{ background: color + 'cc' }}
          >
            {ticket.typeName}
          </span>
        </div>
      )}

      {/* Stub + Body */}
      <div className="flex flex-1">
        {/* Stub */}
        <div
          className="relative w-16 flex-shrink-0 flex items-center justify-center"
          style={{ background: color }}
        >
          {/* Notch top */}
          <div className="absolute -right-3 top-0 w-6 h-6 rounded-full bg-gray-50 z-10" />
          {/* Notch bottom */}
          <div className="absolute -right-3 bottom-0 w-6 h-6 rounded-full bg-gray-50 z-10" />
          {/* Dashed separator */}
          <div className="absolute right-0 top-3 bottom-3 border-r-2 border-dashed border-white/40" />
          <p
            className="text-white font-black text-xs tracking-[0.2em] select-none"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {ticket.code}
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 p-5 min-w-0">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <h3 className="text-gray-900 font-bold text-xl leading-tight truncate">{ticket.name}</h3>
              {!ticket.imageUrl && (
                <span
                  className="inline-block mt-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full"
                  style={{ background: color + '18', color }}
                >
                  {ticket.typeName}
                </span>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="font-black text-2xl leading-none" style={{ color }}>
                ৳{Number(ticket.price).toLocaleString()}
              </p>
              <p className="text-gray-400 text-xs mt-0.5">per ticket</p>
            </div>
          </div>

          {/* Description */}
          {ticket.description && (
            <p className="text-gray-500 text-sm mb-3 leading-relaxed">{ticket.description}</p>
          )}

          {/* Inclusions */}
          {ticket.inclusions?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {ticket.inclusions.map((inc, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full"
                  style={{ background: color + '15', color }}
                >
                  <Check size={9} strokeWidth={3} />
                  {inc}
                </span>
              ))}
            </div>
          )}

          {/* Min qty notice */}
          {ticket.typeId === 1 && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-3 inline-block">
              Minimum {ticket.minQuantity} tickets required per order
            </p>
          )}

          {/* Divider */}
          <div className="border-t border-gray-100 pt-4 mt-1 flex items-center justify-between">
            {/* Stepper */}
            <div
              className="flex items-center rounded-xl overflow-hidden border-2"
              style={{ borderColor: color + '40' }}
            >
              <button
                type="button"
                onClick={() => onQtyChange(Math.max(0, qty - 1))}
                disabled={qty === 0}
                className="w-9 h-9 flex items-center justify-center transition-colors disabled:opacity-30 hover:opacity-80"
                style={{ background: color + '10' }}
              >
                <Minus size={13} style={{ color }} />
              </button>
              <span
                className="w-12 text-center font-bold text-base"
                style={{ color: qty > 0 ? color : '#9ca3af' }}
              >
                {qty}
              </span>
              <button
                type="button"
                onClick={() => onQtyChange(qty + 1)}
                className="w-9 h-9 flex items-center justify-center transition-colors hover:opacity-80"
                style={{ background: color + '10' }}
              >
                <Plus size={13} style={{ color }} />
              </button>
            </div>

            {/* Running total */}
            {qty > 0 ? (
              <div className="text-right">
                <p className="text-xs text-gray-400">{qty} ticket{qty > 1 ? 's' : ''}</p>
                <p className="font-bold text-gray-800 text-lg leading-tight">
                  ৳{(qty * ticket.price).toLocaleString()}
                </p>
              </div>
            ) : (
              <p className="text-gray-300 text-sm">Add to order</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Step Indicator
───────────────────────────────────────────────────────────── */
const StepIndicator = ({ step, primaryColor }) => (
  <div className="flex items-start justify-center mb-12">
    {STEPS.map((s, i) => (
      <React.Fragment key={i}>
        <div className="flex flex-col items-center gap-2" style={{ width: '7rem' }}>
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm transition-all"
            style={
              i < step
                ? { background: '#22c55e', color: '#fff' }
                : i === step
                ? { background: primaryColor, color: '#fff', boxShadow: `0 0 0 5px ${primaryColor}25` }
                : { background: '#fff', border: '2px solid #e5e7eb', color: '#9ca3af' }
            }
          >
            {i < step ? <Check size={17} /> : i + 1}
          </div>
          <span
            className="text-xs text-center leading-tight font-medium"
            style={{
              color: i < step ? '#16a34a' : i === step ? '#111827' : '#9ca3af',
              fontWeight: i === step ? 600 : 500,
            }}
          >
            {s}
          </span>
        </div>
        {i < STEPS.length - 1 && (
          <div
            className="h-0.5 mt-5 mx-1 transition-all"
            style={{
              flex: 1,
              maxWidth: '5rem',
              background: i < step ? '#22c55e' : '#e5e7eb',
            }}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

/* ─────────────────────────────────────────────────────────────
   Form Input helper
───────────────────────────────────────────────────────────── */
const FormField = ({ label, optional, children }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
      {label}{optional && <span className="text-gray-400 font-normal ml-1">(optional)</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-gray-900 placeholder:text-gray-400";

/* ─────────────────────────────────────────────────────────────
   Main Page
───────────────────────────────────────────────────────────── */
const Tickets = () => {
  const { hotel } = useHotel();
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [quantities, setQuantities] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [leadForm, setLeadForm] = useState({ leadName: '', leadEmail: '', leadPhone: '', visitDate: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
    apiClient.get('/api/Ticket/GetPage')
      .then(data => {
        if (!data?.isTicketEnabled) { navigate('/'); return; }
        setPageData(data);
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const selectedItems = pageData?.ticketTypes
    ?.filter(t => (quantities[t.id] || 0) > 0)
    .map(t => ({ ...t, qty: quantities[t.id] })) || [];

  const grandTotal = selectedItems.reduce((sum, t) => sum + t.qty * t.price, 0);
  const totalTickets = selectedItems.reduce((sum, t) => sum + t.qty, 0);

  const canProceedStep0 = () => {
    if (selectedItems.length === 0) return false;
    return selectedItems.every(item => item.qty >= item.minQuantity);
  };

  const validateStep0Error = () => {
    for (const item of selectedItems) {
      if (item.qty > 0 && item.qty < item.minQuantity)
        return `"${item.name}" requires a minimum of ${item.minQuantity} tickets.`;
    }
    return '';
  };

  const handleContinue = () => {
    const err = validateStep0Error();
    if (err) { setError(err); return; }
    setError('');
    if (!isLoggedIn) { setShowAuthGate(true); return; }
    if (user && !leadForm.leadName && !leadForm.leadEmail) {
      setLeadForm(p => ({ ...p, leadName: user.name || '', leadEmail: user.userId || '' }));
    }
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (new Date(leadForm.visitDate) < new Date(new Date().toDateString())) {
      setError('Visit date cannot be in the past.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        leadName: leadForm.leadName,
        leadEmail: leadForm.leadEmail,
        leadPhone: leadForm.leadPhone || null,
        visitDate: leadForm.visitDate,
        items: selectedItems.map(t => ({ ticketTypeId: t.id, quantity: t.qty })),
      };
      const order = await apiClient.post('/api/Ticket/Book', payload);
      navigate(`/tickets/order/${order.orderRef}`);
    } catch (err) {
      setError(err.message || 'Failed to place order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const primaryColor = hotel?.primaryColor || '#1e40af';

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Auth Gate Modal ── */}
      {showAuthGate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowAuthGate(false)}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Colored top strip */}
            <div className="h-2 w-full" style={{ background: primaryColor }} />

            <div className="p-8">
              <button
                onClick={() => setShowAuthGate(false)}
                className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>

              <div className="text-center mb-7">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: primaryColor + '15' }}
                >
                  <Lock size={26} style={{ color: primaryColor }} />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Sign In Required</h2>
                <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                  Please sign in or create a free account to complete your ticket purchase.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/login', { state: { from: '/tickets' } })}
                  className="w-full h-11 font-semibold"
                  style={{ background: primaryColor }}
                >
                  Sign In to Continue
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/register', { state: { from: '/tickets' } })}
                  className="w-full h-11 font-semibold border-gray-200 text-gray-700 hover:bg-gray-50"
                >
                  Create Free Account
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Hero ── */}
      <section
        className="relative h-64 sm:h-72 flex items-end bg-cover bg-center"
        style={{
          backgroundImage: pageData?.bannerImageUrl
            ? `linear-gradient(to bottom, ${primaryColor}80, ${primaryColor}f0), url(${pageData.bannerImageUrl})`
            : `linear-gradient(135deg, ${primaryColor}ee, ${primaryColor}aa)`,
        }}
      >
        {/* Decorative dots */}
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }}
        />
        <div className="relative container max-w-5xl mx-auto px-6 pb-10">
          <div className="flex items-center gap-2 mb-2">
            <Ticket size={18} className="text-white/70" />
            <span className="text-white/70 text-sm font-medium uppercase tracking-widest">Resort Tickets</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-white leading-tight">
            {pageData?.pageTitle || 'Resort Tickets'}
          </h1>
          {pageData?.subTitle && (
            <p className="text-white/80 text-lg mt-2">{pageData.subTitle}</p>
          )}
        </div>
        {/* Curved bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gray-50" style={{ borderRadius: '50% 50% 0 0 / 100% 100% 0 0' }} />
      </section>

      <div className="container max-w-5xl mx-auto px-4 pt-8 pb-20">

        <StepIndicator step={step} primaryColor={primaryColor} />

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
            <X size={16} className="flex-shrink-0" /> {error}
          </div>
        )}

        {/* ── Step 0: Select Tickets ── */}
        {step === 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

            {/* Ticket list */}
            <div className="lg:col-span-2 space-y-5 pb-6">
              {pageData?.description && (
                <p className="text-gray-500 leading-relaxed">{pageData.description}</p>
              )}
              {(pageData?.ticketTypes || []).map(t => (
                <TicketCard
                  key={t.id}
                  ticket={t}
                  qty={quantities[t.id] || 0}
                  onQtyChange={qty => setQuantities(p => ({ ...p, [t.id]: qty }))}
                />
              ))}
            </div>

            {/* Cart sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
                {/* Header */}
                <div className="px-5 pt-5 pb-4 border-b border-gray-100">
                  <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                    <ShoppingCart size={17} style={{ color: primaryColor }} />
                    Your Selection
                  </h3>
                </div>

                <div className="p-5">
                  {selectedItems.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Tag size={20} className="text-gray-400" />
                      </div>
                      <p className="text-gray-400 text-sm">No tickets selected yet</p>
                      <p className="text-gray-300 text-xs mt-1">Add tickets from the left</p>
                    </div>
                  ) : (
                    <div className="space-y-3 mb-4">
                      {selectedItems.map(t => (
                        <div key={t.id}>
                          <div className="flex justify-between items-center text-sm">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: t.color || '#2563EB' }} />
                              <p className="font-medium text-gray-800 truncate">{t.qty}× {t.name}</p>
                            </div>
                            <span className="font-semibold text-gray-900 ml-2 flex-shrink-0">
                              ৳{(t.qty * t.price).toLocaleString()}
                            </span>
                          </div>
                          {t.qty < t.minQuantity && (
                            <p className="text-red-500 text-xs mt-0.5 ml-4">Min {t.minQuantity} required</p>
                          )}
                        </div>
                      ))}

                      {/* Total */}
                      <div className="pt-3 mt-1 border-t border-dashed border-gray-200">
                        <div className="flex justify-between items-baseline">
                          <span className="text-gray-500 text-sm">{totalTickets} ticket{totalTickets > 1 ? 's' : ''}</span>
                          <span className="font-black text-2xl" style={{ color: primaryColor }}>
                            ৳{grandTotal.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* CTA */}
                  <button
                    onClick={handleContinue}
                    disabled={!canProceedStep0()}
                    className="w-full h-11 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: canProceedStep0() ? primaryColor : '#9ca3af' }}
                  >
                    Continue <ArrowRight size={16} />
                  </button>

                  {/* Auth hint */}
                  <div className="mt-3">
                    {!isLoggedIn ? (
                      <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <Lock size={11} className="flex-shrink-0" />
                        <span>Sign in required to continue</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                        <Check size={11} strokeWidth={3} className="flex-shrink-0" />
                        <span>Signed in as <span className="font-semibold">{user?.name}</span></span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 1: Lead Info ── */}
        {step === 1 && (
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
              <div
                className="px-6 py-5 border-b border-gray-100"
                style={{ background: primaryColor + '08' }}
              >
                <h2 className="text-xl font-bold text-gray-900">Your Details</h2>
                <p className="text-gray-500 text-sm mt-0.5">We'll send your ticket confirmation to this email.</p>
              </div>

              <div className="p-6">
                <form
                  onSubmit={e => { e.preventDefault(); setStep(2); window.scrollTo(0, 0); }}
                  className="space-y-5"
                >
                  <FormField label="Full Name">
                    <input
                      value={leadForm.leadName}
                      onChange={e => setLeadForm(p => ({ ...p, leadName: e.target.value }))}
                      className={inputCls}
                      placeholder="Your full name"
                      required
                    />
                  </FormField>

                  <FormField label="Email Address">
                    <input
                      type="email"
                      value={leadForm.leadEmail}
                      onChange={e => setLeadForm(p => ({ ...p, leadEmail: e.target.value }))}
                      className={inputCls}
                      placeholder="you@email.com"
                      required
                    />
                  </FormField>

                  <FormField label="Phone" optional>
                    <input
                      value={leadForm.leadPhone}
                      onChange={e => setLeadForm(p => ({ ...p, leadPhone: e.target.value }))}
                      className={inputCls}
                      placeholder="+880 1XXX-XXXXXX"
                    />
                  </FormField>

                  <FormField label="Visit Date">
                    <input
                      type="date"
                      value={leadForm.visitDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={e => setLeadForm(p => ({ ...p, visitDate: e.target.value }))}
                      className={inputCls}
                      required
                    />
                  </FormField>

                  <div className="flex gap-3 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(0)}
                      className="flex-1 border-gray-200 text-gray-600 h-11"
                    >
                      <ArrowLeft size={15} className="mr-1.5" /> Back
                    </Button>
                    <button
                      type="submit"
                      className="flex-1 h-11 rounded-xl font-semibold text-white flex items-center justify-center gap-2"
                      style={{ background: primaryColor }}
                    >
                      Review Order <ArrowRight size={15} />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Summary + Pay ── */}
        {step === 2 && (
          <div className="max-w-lg mx-auto space-y-5">

            {/* Order summary card */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">Order Summary</h2>
                <button
                  onClick={() => setStep(0)}
                  className="text-xs font-medium hover:underline"
                  style={{ color: primaryColor }}
                >
                  Edit
                </button>
              </div>

              <div className="p-6 space-y-3">
                {selectedItems.map(t => (
                  <div key={t.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: (t.color || '#2563EB') + '20' }}>
                        <Ticket size={14} style={{ color: t.color || '#2563EB' }} />
                      </div>
                      <div>
                        <p className="text-gray-800 text-sm font-medium">{t.name}</p>
                        <p className="text-gray-400 text-xs">{t.qty} × ৳{Number(t.price).toLocaleString()}</p>
                      </div>
                    </div>
                    <span className="font-bold text-gray-900">৳{(t.qty * t.price).toLocaleString()}</span>
                  </div>
                ))}

                <div className="pt-3 mt-1 border-t border-dashed border-gray-200 flex justify-between items-baseline">
                  <span className="text-gray-600 font-medium">{totalTickets} ticket{totalTickets > 1 ? 's' : ''} total</span>
                  <span className="font-black text-2xl" style={{ color: primaryColor }}>
                    ৳{grandTotal.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Lead info */}
              <div className="mx-6 mb-6 rounded-xl p-4 space-y-2" style={{ background: primaryColor + '08' }}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Contact Details</p>
                <div className="flex items-center gap-2.5 text-sm text-gray-700">
                  <Users size={14} style={{ color: primaryColor }} className="flex-shrink-0" />
                  {leadForm.leadName}
                </div>
                <div className="flex items-center gap-2.5 text-sm text-gray-700">
                  <Mail size={14} style={{ color: primaryColor }} className="flex-shrink-0" />
                  {leadForm.leadEmail}
                </div>
                {leadForm.leadPhone && (
                  <div className="flex items-center gap-2.5 text-sm text-gray-700">
                    <Phone size={14} style={{ color: primaryColor }} className="flex-shrink-0" />
                    {leadForm.leadPhone}
                  </div>
                )}
                <div className="flex items-center gap-2.5 text-sm text-gray-700">
                  <Calendar size={14} style={{ color: primaryColor }} className="flex-shrink-0" />
                  Visit date: <span className="font-medium ml-1">{leadForm.visitDate}</span>
                </div>
              </div>
            </div>

            {/* Payment placeholder */}
            <div
              className="rounded-2xl p-5 text-center border"
              style={{ background: primaryColor + '08', borderColor: primaryColor + '30' }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                style={{ background: primaryColor + '20' }}
              >
                <Ticket size={22} style={{ color: primaryColor }} />
              </div>
              <h3 className="font-bold text-gray-800 mb-1">Payment Coming Soon</h3>
              <p className="text-gray-500 text-sm">
                Click below to get your tickets. Payment integration will be added shortly.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              {error && (
                <p className="text-red-600 text-sm flex items-center gap-1.5">
                  <X size={14} /> {error}
                </p>
              )}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 border-gray-200 text-gray-600 h-11"
                >
                  <ArrowLeft size={15} className="mr-1.5" /> Back
                </Button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 h-11 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ background: primaryColor }}
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <>Get Tickets <ArrowRight size={15} /></>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tickets;
